import { parseCSV } from './parsers/genericParser.js';
import {
  saveTransactionsForFile,
  markFileAsProcessed,
  isFileProcessed,
  getRules,
  getCategories,
  getTransactions,
  resolveCategoryAlias
} from './storage.js';
import { createDefaultPipeline, categorizeMany, type PipelineState } from './categorization/index.js';
import type { Transaction } from '../types/transaction';

export interface ImportAnalysis {
  ruleSuggestions: {
    duplicateSuggestions: any[];
    extensionSuggestions: any[];
  };
  uncategorizedTransactions: Transaction[];
  mediumConfidenceTransactions: Transaction[];
  recurringPatterns: any[];
  hasActions: boolean;
}

export interface UploadResult {
  success: boolean;
  newTransactions: Transaction[];
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  duplicatesSkipped?: number;
  message: string;
  analysis?: ImportAnalysis;
}

/**
 * Create and initialize the categorization pipeline
 */
export function createCategorizationPipeline() {
  return createDefaultPipeline({
    minConfidence: 0.6
  });
}

/**
 * Apply categorization pipeline to transactions after parsing
 * Also normalizes categories through alias resolution
 */
export async function applyCategorizationToTransactions(
  transactions: Transaction[],
  pipeline: PipelineState
): Promise<Transaction[]> {
  if (!pipeline) {
    console.warn('Categorization pipeline not initialized');
    return transactions;
  }

  const rules = await getRules();
  const categories = await getCategories();

  const context = {
    rules,
    categories
  };

  // Run categorization pipeline
  const categorizedTransactions = await categorizeMany(pipeline, transactions, context);

  // Resolve category aliases - handles renamed/deleted categories
  // When HybridCategorizer returns "Groceries" but user renamed it to "Food Shopping",
  // this resolves through the alias chain to assign the correct current category name
  const resolvedTransactions = await Promise.all(
    categorizedTransactions.map(async (txn) => ({
      ...txn,
      category: await resolveCategoryAlias(txn.category)
    }))
  );

  return resolvedTransactions;
}

/**
 * Process a single uploaded file
 */
export async function processUploadedFile(
  file: File,
  pipeline: PipelineState
): Promise<{ transactions: Transaction[]; skipped: boolean; duplicatesSkipped?: number }> {
  // Check if already processed
  if (await isFileProcessed(file.name)) {
    return { transactions: [], skipped: true };
  }

  const text = await file.text();
  let transactions = parseCSV(text, file.name);

  // Apply categorization
  transactions = await applyCategorizationToTransactions(transactions, pipeline);

  if (transactions.length > 0) {
    const saveResult = await saveTransactionsForFile(file.name, transactions);
    if (saveResult) {
      // saveResult might be true (old API) or an object with duplicate info (new API)
      const duplicatesSkipped = typeof saveResult === 'object' ? saveResult.duplicatesSkipped : 0;
      const savedCount = typeof saveResult === 'object' ? saveResult.count : transactions.length;

      // Mark file as processed with valid count (must not be null)
      if (savedCount > 0) {
        await markFileAsProcessed(file.name, savedCount);
      }
      return {
        transactions: transactions.slice(0, savedCount),
        skipped: false,
        duplicatesSkipped
      };
    }
  }

  return { transactions: [], skipped: false };
}

/**
 * Process multiple uploaded files
 */
export async function processUploadedFiles(
  files: FileList,
  pipeline: PipelineState
): Promise<UploadResult> {
  let allNewTransactions: Transaction[] = [];
  let processedCount = 0;
  let skippedCount = 0;
  let totalDuplicates = 0;

  for (const file of Array.from(files)) {
    try {
      const result = await processUploadedFile(file, pipeline);

      if (result.skipped) {
        skippedCount++;
      } else if (result.transactions.length > 0) {
        allNewTransactions = [...allNewTransactions, ...result.transactions];
        processedCount++;
        if (result.duplicatesSkipped) {
          totalDuplicates += result.duplicatesSkipped;
        }
      }
    } catch (error) {
      console.error(`Error processing file ${file.name}:`, error);
    }
  }

  // Analyze imported transactions if any were imported
  let analysis: ImportAnalysis | undefined;
  if (allNewTransactions.length > 0) {
    analysis = await analyzeImport(allNewTransactions);
  }

  return buildUploadResult(allNewTransactions, processedCount, skippedCount, 0, totalDuplicates, analysis);
}



/**
 * Build upload result message
 */
/**
 * Analyze imported transactions for recommended actions
 */
async function analyzeImport(allTransactions: Transaction[]): Promise<ImportAnalysis> {
  try {
    // Filter medium-confidence transactions (0.75-0.85)
    // These are candidates for user review and rule creation
    const mediumConfidenceTransactions = allTransactions.filter(t => {
      const confidence = t.confidence || 0;
      return confidence >= 0.75 && confidence < 0.85 && t.category !== 'Uncategorized';
    });

    console.log(`Found ${mediumConfidenceTransactions.length} medium-confidence transactions for review`);

    // PWA migration: Stub rule suggestions (advanced feature disabled for now)
    const ruleSuggestionsResult = {
      duplicateSuggestions: [],
      extensionSuggestions: [],
      totalSuggestions: 0
    };

    // PWA migration: Get uncategorized transactions from storage provider
    const allTransactionsFromDB = await getTransactions();
    const uncategorizedResult = {
      transactions: allTransactionsFromDB.filter(t => t.category === 'Uncategorized'),
      count: allTransactionsFromDB.filter(t => t.category === 'Uncategorized').length
    };

    // PWA migration: Stub recurring detection (advanced feature disabled for now)
    const recurringResult = { patterns: [] };

    const analysis: ImportAnalysis = {
      ruleSuggestions: {
        duplicateSuggestions: ruleSuggestionsResult.duplicateSuggestions || [],
        extensionSuggestions: ruleSuggestionsResult.extensionSuggestions || []
      },
      uncategorizedTransactions: uncategorizedResult.transactions || [],
      mediumConfidenceTransactions,
      recurringPatterns: recurringResult.patterns || [],
      hasActions: false
    };

    // Check if there are any actions to take
    analysis.hasActions =
      analysis.mediumConfidenceTransactions.length > 0 ||
      analysis.ruleSuggestions.duplicateSuggestions.length > 0 ||
      analysis.ruleSuggestions.extensionSuggestions.length > 0 ||
      analysis.recurringPatterns.length > 0;

    return analysis;
  } catch (error) {
    console.error('Error analyzing import:', error);
    // Return empty analysis if something fails
    return {
      ruleSuggestions: { duplicateSuggestions: [], extensionSuggestions: [] },
      uncategorizedTransactions: [],
      mediumConfidenceTransactions: [],
      recurringPatterns: [],
      hasActions: false
    };
  }
}

function buildUploadResult(
  transactions: Transaction[],
  processedCount: number,
  skippedCount: number,
  failedCount: number,
  duplicatesSkipped: number = 0,
  analysis?: ImportAnalysis
): UploadResult {
  if (transactions.length > 0) {
    let message = `Successfully imported ${transactions.length} transactions from ${processedCount} file(s).`;
    if (duplicatesSkipped > 0) {
      message += ` ${duplicatesSkipped} duplicate transaction(s) were automatically skipped.`;
    }
    if (skippedCount > 0) {
      message += ` ${skippedCount} file(s) were already processed.`;
    }
    if (failedCount > 0) {
      message += ` ${failedCount} file(s) could not be loaded.`;
    }

    return {
      success: true,
      newTransactions: transactions,
      processedCount,
      skippedCount,
      failedCount,
      duplicatesSkipped,
      message,
      analysis
    };
  } else if (skippedCount > 0) {
    return {
      success: true,
      newTransactions: [],
      processedCount,
      skippedCount,
      failedCount,
      duplicatesSkipped,
      message: `All files are already processed. Check the account breakdown for details.`,
      analysis
    };
  } else {
    return {
      success: false,
      newTransactions: [],
      processedCount,
      skippedCount,
      failedCount,
      duplicatesSkipped,
      message: 'No valid transactions found in the uploaded files.',
      analysis
    };
  }
}
